import { XIcon } from '@phosphor-icons/react'
import { useTransition } from 'react'

import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Overlay } from '@/components/ui/overlay'
import { Colors } from '@/theme/defaults'

import * as S from './confirmation-popup.styled'
import { ConfirmationPopupProps } from './confirmation-popup.types'

const CONFIRM_ICON_COLOR: Record<S.ConfirmationVariant, keyof Colors> = {
  danger: 'red600',
  info: 'white',
  success: 'green500',
  warning: 'orange500',
}

export const ConfirmationPopup = ({
  children,
  onClose,
  onConfirm,
  title,
  headerIcon,
  closeable = true,
  closeOnOverlayClick = true,
  closeOnEscapeKey = true,
  variant = 'danger',
  cancelText = 'Cancel',
  confirmText = 'Confirm',
  autoWidth,
  confirmIcon,
  autoClose = true,
  ...props
}: ConfirmationPopupProps) => {
  const [isPending, startTransition] = useTransition()
  const isInfo = variant === 'info'

  return (
    <Overlay
      closeable={closeable}
      closeOnOverlayClick={closeOnOverlayClick && !isPending}
      closeOnEscapeKey={closeOnEscapeKey && !isPending}
      role="alertdialog"
      onClose={onClose}
    >
      {({ handleClose, getFloatingProps, setFloating }) => {
        // Note: Error boundary to avoid app crashes.
        // Errors should be handled in the onConfirm callback.
        const handleConfirm = () => {
          startTransition(async () => {
            try {
              await onConfirm()
              if (autoClose) handleClose()
            } catch {
              if (autoClose) handleClose()
            }
          })
        }

        const closeButtonVariant = isInfo ? 'ghost' : 'inverted'

        return (
          <S.Container ref={setFloating} $autoWidth={autoWidth} {...getFloatingProps()} {...props}>
            <S.Header $variant={variant}>
              <S.HeaderTitle>
                {headerIcon && (
                  <S.HeaderIcon aria-hidden="true">
                    <Icon icon={headerIcon} color="white" />
                  </S.HeaderIcon>
                )}
                <S.HeaderText $variant={variant}>{title}</S.HeaderText>
              </S.HeaderTitle>
              <Button
                compact
                use={closeButtonVariant}
                size="small"
                type="button"
                aria-label="Close"
                disabled={!closeable || isPending}
                onClick={handleClose}
              >
                <Icon icon={XIcon} />
              </Button>
            </S.Header>
            <S.Body>{children}</S.Body>
            <S.Footer>
              <Button
                disabled={isPending}
                type="button"
                use="secondary"
                size="medium"
                onClick={handleClose}
              >
                {cancelText}
              </Button>
              <Button
                type="button"
                use={isInfo ? 'primary' : variant}
                size="medium"
                loading={isPending}
                onClick={handleConfirm}
              >
                {confirmIcon && (
                  <Icon icon={confirmIcon} color={CONFIRM_ICON_COLOR[variant]} size="2rem" />
                )}
                {confirmText}
              </Button>
            </S.Footer>
          </S.Container>
        )
      }}
    </Overlay>
  )
}
